// Roland S-50 / S-330 / S-550 / W-30 Floppy Disk Image Exporter
//
// Creates a genuine 720 KB floppy disk image (.img) with 12-bit packed PCM
// samples in the Roland S-50 family disk format.
//
// Disk layout (S-50 family, 720 KB = 737,280 bytes):
//   0x00000 - 0x00003  Zeros (4 bytes)
//   0x00004 - 0x0000B  Volume tag (8 ASCII): "S-50    ", "S330    ", "S550    ", "W-30    "
//   0x00010 - 0x000FF  Volume info lines (FE-terminated, up to 6 lines × 32 bytes)
//   0x0FC00 - 0x0FFFF  16 patches × 12-byte names (padded to 512-byte boundary)
//   0x10E00 - 0x11DFF  32 tone-parameter blocks × 128 bytes
//   0x11E00 - 0x11FFF  32 tone-directory entries × 16 bytes
//   0x12000 - 0x6FFFF  Bank A: 18 blocks × 18432 bytes (12-bit packed PCM)
//   0x20000 - 0xB3FFF  Bank B: 18 blocks × 18432 bytes (12-bit packed PCM)
//
// 12-bit packed PCM (3 bytes → 2 samples):
//   Each 16-bit signed sample is right-shifted by 4 to produce a 12-bit value.
//   v1 = (s1 >> 4) & 0xFFF, v2 = (s2 >> 4) & 0xFFF
//   b0 = (v1 >> 4) & 0xFF
//   b1 = ((v1 & 0x0F) << 4) | (v2 & 0x0F)
//   b2 = (v2 >> 4) & 0xFF
//
// Sample rates: 15 kHz (rateFlag=1) or 30 kHz (rateFlag=0)
//
// Format reference: gordonjcp/s50tools (GPL) + llamamusic.com docs.
// Reverse-engineered from the roland-parser.cjs unpackSDiskBlock() function.

// ---- Disk geometry ---------------------------------------------------------
export const SDISK_DISK_SIZE   = 737280;   // 720 KB floppy (80 tracks × 9 × 512 × 2 ... actually 720*1024)
export const SDISK_BLOCK_BYTES = 18432;    // 12-bit packed PCM per block
export const SDISK_SAMPLES_PER_BLOCK = 12288; // unpacked 16-bit samples per block
export const SDISK_BLOCKS_PER_BANK = 18;
export const SDISK_BANK_BYTES  = SDISK_BLOCKS_PER_BANK * SDISK_BLOCK_BYTES; // 331,776
export const SDISK_MAX_BLOCKS  = SDISK_BLOCKS_PER_BANK * 2; // 36 total blocks
export const SDISK_MAX_TONES   = 32;
export const SDISK_MAX_PATCHES = 16;

// ---- Disk offsets ----------------------------------------------------------
export const SDISK_OFFSETS = {
   PATCH:    0x0FC00,
   TONE:     0x10E00,
   TONEDIR:  0x11E00,
   SAMPLE:   0x12000,
};

// ---- Volume tags -----------------------------------------------------------
const VOLUME_TAGS = {
   'S-50':  'S-50    ',
   'S-330': 'S330    ',
   'S-550': 'S550    ',
   'W-30':  'W-30    ',
};

// ---- Sample rates ----------------------------------------------------------
export const SDISK_SAMPLE_RATES = [
   { rate: 30000, rateFlag: 0, label: '30 kHz (high quality, ~14.7s total)' },
   { rate: 15000, rateFlag: 1, label: '15 kHz (long samples, ~29.5s total)' },
];

/**
 * Find the rate flag for a given sample rate.
 */
export function sdiskRateFlag(rate) {
   for (const r of SDISK_SAMPLE_RATES) {
      if (r.rate === rate) return r.rateFlag;
   }
   return 0; // default to 30 kHz
}

/**
 * Pack a group of 2 samples (16-bit signed) into 3 bytes (12-bit packed).
 *
 * @param {Uint8Array} out - output buffer
 * @param {number} byteOff - byte offset in output
 * @param {number} s0 - first 16-bit signed sample
 * @param {number} s1 - second 16-bit signed sample
 */
function packSDiskGroup(out, byteOff, s0, s1) {
   const v0 = (s0 >> 4) & 0xFFF;
   const v1 = (s1 >> 4) & 0xFFF;
   out[byteOff]     = (v0 >> 4) & 0xFF;
   out[byteOff + 1] = ((v0 & 0x0F) << 4) | (v1 & 0x0F);
   out[byteOff + 2] = (v1 >> 4) & 0xFF;
}

/**
 * Pack 16-bit PCM samples into one 18432-byte block (12288 samples).
 * If fewer samples are provided, the remainder is silence (zero-padded).
 *
 * @param {Int16Array} pcm - input PCM (up to 12288 samples)
 * @param {number} srcOffset - offset into pcm array
 * @returns {Uint8Array} 18432-byte packed block
 */
function packSDiskBlock(pcm, srcOffset) {
   const out = new Uint8Array(SDISK_BLOCK_BYTES);
   const maxSamples = Math.min(SDISK_SAMPLES_PER_BLOCK, pcm.length - srcOffset);
   let j = 0;
   for (let i = 0; i < maxSamples; i += 2) {
      const s0 = pcm[srcOffset + i];
      const s1 = (i + 1 < maxSamples) ? pcm[srcOffset + i + 1] : 0;
      packSDiskGroup(out, j, s0, s1);
      j += 3;
   }
   return out;
}

/**
 * Calculate the number of 12-bit packed blocks needed for a given sample count.
 *
 * @param {number} numSamples - number of 16-bit PCM samples
 * @returns {number} block count (each block = 12288 samples)
 */
function blocksForSamples(numSamples) {
   if (numSamples <= 0) return 0;
   return Math.ceil(numSamples / SDISK_SAMPLES_PER_BLOCK);
}

/**
 * Calculate the total packed disk size for a set of samples.
 * This is always the fixed 720 KB floppy size, but we return the
 * effective sample data usage for capacity bar purposes.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - total sample data bytes (packed, including padding)
 */
export function calcSDiskCapacity(samples) {
   let totalBlocks = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      totalBlocks += blocksForSamples(smp.pcm.length);
   }
   return totalBlocks * SDISK_BLOCK_BYTES;
}

/**
 * Calculate how many blocks are used and in which bank.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {{ totalBlocks: number, bankA: number, bankB: number, fits: boolean }}
 */
export function calcSDiskLayout(samples) {
   let totalBlocks = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      totalBlocks += blocksForSamples(smp.pcm.length);
   }
   const bankA = Math.min(totalBlocks, SDISK_BLOCKS_PER_BANK);
   const bankB = Math.max(0, totalBlocks - SDISK_BLOCKS_PER_BANK);
   const fits = totalBlocks <= SDISK_MAX_BLOCKS;
   return { totalBlocks, bankA, bankB, fits };
}

/**
 * Write an ASCII string into a buffer at a given offset, space-padded.
 *
 * @param {Uint8Array} buf - target buffer
 * @param {number} off - offset
 * @param {number} len - field length
 * @param {string} str - string to write
 */
function writeName(buf, off, len, str) {
   const name = (str || '').substring(0, len).padEnd(len, ' ');
   for (let i = 0; i < len; i++) {
      buf[off + i] = name.charCodeAt(i) & 0x7F;
   }
}

/**
 * Build a complete Roland S-50 family floppy disk image.
 *
 * Each sample becomes a tone. Tones are assigned to bank A first (up to 18
 * blocks), then bank B. A single patch is created that references all tones.
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate }
 * @param {object} opts - { family: 'S-50'|'S-330'|'S-550'|'W-30', volumeName: string, rate: 15000|30000 }
 * @returns {Uint8Array} 737,280-byte disk image
 */
export function buildSDiskDisk(samples, opts = {}) {
   const family = opts.family || 'S-550';
   const volumeName = (opts.volumeName || 'XTRC').substring(0, 11);
   const rate = opts.rate || 30000;
   const rateFlag = sdiskRateFlag(rate);

   const disk = new Uint8Array(SDISK_DISK_SIZE);

   // ---- Volume header (0x00 - 0xFF) -----------------------------------------
   // Bytes 0-3: zeros (already zero)
   // Bytes 4-11: volume tag
   const tag = VOLUME_TAGS[family] || VOLUME_TAGS['S-550'];
   writeName(disk, 0x04, 8, tag);

   // Bytes 0x10-0x7F: volume info lines (FE-terminated)
   // Write 3 info lines: volume name, rate, and a blank line
   const infoLines = [
      volumeName,
      `${rate / 1000} kHz`,
      '',
   ];
   let infoOff = 0x10;
   for (const line of infoLines) {
      const lineBytes = new TextEncoder().encode(line);
      for (let i = 0; i < lineBytes.length && i < 31; i++) {
         disk[infoOff + i] = lineBytes[i];
      }
      disk[infoOff + lineBytes.length] = 0xFE; // line terminator
      infoOff += 32;
   }

   // ---- Patch names (0x0FC00) -----------------------------------------------
   // 16 patches × 12 bytes. Create one patch named after the volume.
   writeName(disk, SDISK_OFFSETS.PATCH, 12, volumeName.substring(0, 12));

   // ---- Tone assignment -----------------------------------------------------
   // Assign each sample to consecutive blocks, filling bank A first then B.
   const tones = [];
   let bankABlock = 0;
   let bankBBlock = 0;

   for (let i = 0; i < samples.length && i < SDISK_MAX_TONES; i++) {
      const smp = samples[i];
      if (!smp.pcm || smp.pcm.length === 0) continue;

      const numBlocks = blocksForSamples(smp.pcm.length);
      if (numBlocks === 0) continue;

      // Determine which bank to place this tone in
      let bank, startBlock;
      if (bankABlock + numBlocks <= SDISK_BLOCKS_PER_BANK) {
         bank = 0; // Bank A
         startBlock = bankABlock;
         bankABlock += numBlocks;
      } else if (bankBBlock + numBlocks <= SDISK_BLOCKS_PER_BANK) {
         bank = 1; // Bank B
         startBlock = bankBBlock;
         bankBBlock += numBlocks;
      } else {
         // No more space — stop adding tones
         break;
      }

      const toneName = (smp.name || `Tone ${i + 1}`).substring(0, 8);
      const origKey = smp.origKey || 60; // C4 = middle C

      tones.push({
         index: tones.length,
         name: toneName,
         bank,
         startBlock,
         numBlocks,
         pcm: smp.pcm,
         rateFlag,
         origKey,
      });
   }

   if (tones.length === 0) {
      throw new Error('no valid samples to export');
   }

   // ---- Tone parameter blocks (0x10E00) -------------------------------------
   // 32 blocks × 128 bytes. First 8 bytes = name, then parameters.
   for (let i = 0; i < SDISK_MAX_TONES; i++) {
      const off = SDISK_OFFSETS.TONE + i * 128;
      if (i < tones.length) {
         const t = tones[i];
         writeName(disk, off, 8, t.name);
         disk[off + 8]  = 0;    // output assign (output 1)
         disk[off + 9]  = i;    // source tone = self
         disk[off + 10] = 0;    // org sub tone
         disk[off + 11] = t.rateFlag; // sampling frequency
         disk[off + 12] = t.origKey;  // orig key
         disk[off + 13] = t.bank;     // wave bank
         disk[off + 14] = t.startBlock; // segment top
         disk[off + 15] = t.numBlocks;  // segment length
         // Bytes 16-127: tone parameters (envelope, filter, etc.) — zeros = defaults
      }
      // Unused tone slots remain zero (no name → skipped by parser)
   }

   // ---- Tone directory entries (0x11E00) ------------------------------------
   // 32 entries × 16 bytes.
   for (let i = 0; i < SDISK_MAX_TONES; i++) {
      const off = SDISK_OFFSETS.TONEDIR + i * 16;
      if (i < tones.length) {
         const t = tones[i];
         writeName(disk, off, 8, t.name);
         disk[off + 8]  = 0;    // output
         disk[off + 9]  = 0;    // subtone
         disk[off + 10] = 0;    // subtone flag
         disk[off + 11] = t.rateFlag;   // rate flag (1 = 15 kHz, 0 = 30 kHz)
         disk[off + 12] = t.origKey;    // orig key
         disk[off + 13] = t.bank;       // ram bank (0 = A, 1 = B)
         disk[off + 14] = t.startBlock; // start block in bank
         disk[off + 15] = t.numBlocks;  // length in blocks
      }
      // Unused entries remain zero (length=0 → skipped by parser)
   }

   // ---- Sample data (0x12000) -----------------------------------------------
   // Pack PCM into 12-bit blocks, placing each tone at its assigned bank/block.
   for (const t of tones) {
      const bankOff = SDISK_OFFSETS.SAMPLE + t.bank * SDISK_BANK_BYTES;
      for (let blk = 0; blk < t.numBlocks; blk++) {
         const srcOff = blk * SDISK_SAMPLES_PER_BLOCK;
         const packed = packSDiskBlock(t.pcm, srcOff);
         const dstOff = bankOff + (t.startBlock + blk) * SDISK_BLOCK_BYTES;
         disk.set(packed, dstOff);
      }
   }

   console.log(`[Roland SDisk] buildSDiskDisk: ${tones.length} tones, family=${family}, rate=${rate} Hz, ` +
      `bankA=${bankABlock} blocks, bankB=${bankBBlock} blocks, disk=${SDISK_DISK_SIZE} bytes`);
   for (const t of tones) {
      console.log(`[Roland SDisk] tone ${t.index}: name="${t.name}", bank=${t.bank}, ` +
         `start=${t.startBlock}, blocks=${t.numBlocks}, samples=${t.pcm.length}`);
   }

   return disk;
}
